"use strict";
/**
 * Global Application State
 *
 * Single-source-of-truth for the Kalshi Trading UI.
 *
 * Architecture:
 * - `appState` is a plain object (no proxies/observables).
 * - Mutations go through `updateState(partial)`, which shallow-merges
 *   the update and notifies all subscribers synchronously.
 * - Components subscribe via `subscribe(fn)` and receive the full state.
 * - Persistence helpers read/write to `sessionStorage` (settings, session)
 *   and `localStorage` (data source, favourites, custom series).
 *
 * @module state
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_SETTINGS = void 0;
exports.getState = getState;
exports.updateState = updateState;
exports.subscribe = subscribe;
exports.resetState = resetState;
exports.loadSettingsFromStorage = loadSettingsFromStorage;
exports.saveSettingsToStorage = saveSettingsToStorage;
exports.loadDataSourceFromStorage = loadDataSourceFromStorage;
exports.saveDataSourceToStorage = saveDataSourceToStorage;
exports.resetSettingsToDefaults = resetSettingsToDefaults;
exports.loadFavoriteLeagues = loadFavoriteLeagues;
exports.saveFavoriteLeague = saveFavoriteLeague;
exports.getFavoriteLeague = getFavoriteLeague;
exports.clearFavoriteLeague = clearFavoriteLeague;
exports.loadCustomSeries = loadCustomSeries;
exports.saveCustomSeries = saveCustomSeries;
exports.removeCustomSeries = removeCustomSeries;
exports.loadCustomPrefixes = loadCustomPrefixes;
exports.saveCustomPrefix = saveCustomPrefix;
exports.removeCustomPrefix = removeCustomPrefix;
exports.loadCustomSuffixes = loadCustomSuffixes;
exports.saveCustomSuffix = saveCustomSuffix;
exports.removeCustomSuffix = removeCustomSuffix;
exports.getKalshiSeriesFavorite = getKalshiSeriesFavorite;
exports.saveKalshiSeriesFavorite = saveKalshiSeriesFavorite;
exports.clearKalshiSeriesFavorite = clearKalshiSeriesFavorite;
const venues_1 = require("../shared/venues");
// ============================================================================
// Default Settings
// ============================================================================
/** Initial values for every `TradingSettings` field. */
const DEFAULT_SETTINGS = {
    shares: 1,
    primaryOrdersEnabled: true,
    // Off by default; the control is owner-gated (see gates.ts) and the
    // behaviour is server-enforced (auth/levels.ts allowCloseOpposite).
    closeOppositeOnBuy: false,
    dollarAmount: 100,
    optionBPriceLimit: 0.5,
    orderTimeoutSeconds: 60,
    slippageTolerancePercent: 3,
    independentSlippage: false,
    slippageTolerancePercentA: 3,
    slippageTolerancePercentB: 3,
    useDelayedPrice: false,
    manualPriceMode: false,
    manualYesPrice: 50,
    manualYesPriceSelected: false,
    manualNoPrice: 50,
    manualNoPriceSelected: false,
    sellMode: "full",
    fixedSellQuantity: 1,
    baseballMode: false,
    basketballMode: false,
    soccerMode: false,
    forceDefaultButtons: false,
    baseballFactor: 0,
    baseballButtonManualPrices: {},
};
exports.DEFAULT_SETTINGS = DEFAULT_SETTINGS;
// Initial app state
const INITIAL_STATE = {
    isAuthenticated: false,
    sessionToken: null,
    permissions: null,
    currentPage: "login",
    selectedMarket: null,
    parallelMarkets: [],
    tradingSettings: DEFAULT_SETTINGS,
    isLoadingMarkets: false,
    isPlacingOrder: false,
    errorMessage: null,
    lastOrderStatus: null,
    currentPosition: null,
    lastPositionUpdate: 0,
    orderBook: null,
    dataSource: "kalshi",
    polymarketSports: null,
    selectedEventTicker: null,
    availableMarkets: null,
    pairBuilder: {
        search: "",
        selectedKalshiTicker: null,
        selectedPmSlug: null,
        kalshiCategory: "KXNBAGAME",
        pmSport: "",
        pmSports: [],
        kalshiMarkets: [],
        pmUsMarkets: [],
        kalshiLoading: false,
        pmLoading: false,
    },
    monitorStatus: {
        running: false,
        pairCount: 0,
        pairs: [],
        orderMode: "mock",
        lastUpdated: 0,
    },
    venueStatus: null,
};
// ============================================================================
// State Store
// ============================================================================
/** localStorage key for the active data source. */
const DATA_SOURCE_STORAGE_KEY = "kalshi_data_source";
/** The single mutable state object. Never expose directly — use `getState()`. */
let appState = { ...INITIAL_STATE };
const listeners = new Set();
/**
 * Get a shallow copy of the current application state.
 *
 * The returned object is safe to read but mutations will NOT propagate
 * back to the store — use `updateState()` instead.
 */
function getState() {
    return { ...appState };
}
/**
 * Shallow-merge `updates` into the global state and notify all subscribers.
 *
 * Error messages auto-clear after 5 seconds if a non-null `errorMessage`
 * is included in the update.
 */
function updateState(updates) {
    appState = { ...appState, ...updates };
    // Auto-clear error messages after 5 seconds
    if (updates.errorMessage && updates.errorMessage !== null) {
        setTimeout(() => {
            if (appState.errorMessage === updates.errorMessage) {
                updateState({ errorMessage: null });
            }
        }, 5000);
    }
    // Notify all listeners
    listeners.forEach((listener) => listener(appState));
}
/**
 * Register a callback that fires on every state change.
 * Returns an `unsubscribe` function.
 */
function subscribe(listener) {
    listeners.add(listener);
    // Return unsubscribe function
    return () => {
        listeners.delete(listener);
    };
}
/**
 * Reset the entire application state to `INITIAL_STATE` and notify listeners.
 */
function resetState() {
    appState = { ...INITIAL_STATE };
    listeners.forEach((listener) => listener(appState));
}
// ============================================================================
// Settings Persistence (sessionStorage)
// ============================================================================
/**
 * Load trading settings from `sessionStorage`.
 *
 * Handles legacy migration: if `manualPriceMode` is true but
 * `manualYesPriceSelected` / `manualNoPriceSelected` are absent,
 * both are defaulted to `true` for backward compatibility.
 */
function loadSettingsFromStorage() {
    try {
        const stored = sessionStorage.getItem("kalshi_settings");
        if (stored) {
            const settings = JSON.parse(stored);
            const usingLegacyManualPriceSelection = settings.manualPriceMode === true &&
                settings.manualYesPriceSelected === undefined &&
                settings.manualNoPriceSelected === undefined;
            appState.tradingSettings = {
                ...DEFAULT_SETTINGS,
                ...settings,
                manualYesPriceSelected: usingLegacyManualPriceSelection
                    ? true
                    : (settings.manualYesPriceSelected ??
                        DEFAULT_SETTINGS.manualYesPriceSelected),
                manualNoPriceSelected: usingLegacyManualPriceSelection
                    ? true
                    : (settings.manualNoPriceSelected ??
                        DEFAULT_SETTINGS.manualNoPriceSelected),
                baseballButtonManualPrices: settings.baseballButtonManualPrices ?? {},
            };
        }
    }
    catch (error) {
        console.error("Failed to load settings from storage:", error);
    }
}
/** Persist the current trading settings to `sessionStorage`. */
function saveSettingsToStorage() {
    try {
        sessionStorage.setItem("kalshi_settings", JSON.stringify(appState.tradingSettings));
    }
    catch (error) {
        console.error("Failed to save settings to storage:", error);
    }
}
// ============================================================================
// Data Source Persistence (localStorage)
// ============================================================================
/** Load the active data source from `localStorage`. Falls back to `"kalshi"`. */
function loadDataSourceFromStorage() {
    try {
        const stored = localStorage.getItem(DATA_SOURCE_STORAGE_KEY);
        if ((0, venues_1.isDataSource)(stored)) {
            appState.dataSource = stored;
        }
    }
    catch (error) {
        console.error("Failed to load data source from storage:", error);
    }
}
/** Save the current data source to `localStorage`. */
function saveDataSourceToStorage() {
    try {
        localStorage.setItem(DATA_SOURCE_STORAGE_KEY, appState.dataSource);
    }
    catch (error) {
        console.error("Failed to save data source to storage:", error);
    }
}
/** Reset all trading settings to `DEFAULT_SETTINGS`, persist, and notify. */
function resetSettingsToDefaults() {
    appState.tradingSettings = { ...DEFAULT_SETTINGS };
    saveSettingsToStorage();
    listeners.forEach((listener) => listener(appState));
}
// ============================================================================
// Favourite League per Data Source (localStorage)
// ============================================================================
/** localStorage key for the per-source favourite league map. */
const FAVORITE_LEAGUE_STORAGE_KEY = "kalshi_favorite_leagues";
/** Load the favourite-league map `{ [DataSource]: leagueValue }` from localStorage. */
function loadFavoriteLeagues() {
    try {
        const stored = localStorage.getItem(FAVORITE_LEAGUE_STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    }
    catch (error) {
        console.error("Failed to load favorite leagues from storage:", error);
    }
    return {};
}
/** Save a favourite league for a specific data source. */
function saveFavoriteLeague(source, league) {
    try {
        const current = loadFavoriteLeagues();
        current[source] = league;
        localStorage.setItem(FAVORITE_LEAGUE_STORAGE_KEY, JSON.stringify(current));
    }
    catch (error) {
        console.error("Failed to save favorite league to storage:", error);
    }
}
/** Get the favourite league for a specific data source (or `undefined`). */
function getFavoriteLeague(source) {
    return loadFavoriteLeagues()[source];
}
/** Remove the favourite league for a specific data source. */
function clearFavoriteLeague(source) {
    try {
        const current = loadFavoriteLeagues();
        delete current[source];
        localStorage.setItem(FAVORITE_LEAGUE_STORAGE_KEY, JSON.stringify(current));
    }
    catch (error) {
        console.error("Failed to clear favorite league from storage:", error);
    }
}
// ============================================================================
// Custom Kalshi Series Persistence (localStorage)
// ============================================================================
/** localStorage key for user-defined full Kalshi series tickers (PNL page + pair-builder modal). */
const CUSTOM_SERIES_STORAGE_KEY = "kalshi_custom_series";
/** localStorage key for user-defined events-page league prefixes. */
const CUSTOM_PREFIXES_STORAGE_KEY = "kalshi_custom_prefixes";
/** localStorage key for user-defined events-page market-type suffixes. */
const CUSTOM_SUFFIXES_STORAGE_KEY = "kalshi_custom_suffixes";
/** Load a de-duplicated string list from `localStorage`, tolerating bad data. */
function loadStringList(key) {
    try {
        const stored = localStorage.getItem(key);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) {
                return parsed.filter((s) => typeof s === "string" && s.length > 0);
            }
        }
    }
    catch (error) {
        console.error(`Failed to load "${key}" from storage:`, error);
    }
    return [];
}
/** Append a value to a persisted string list. Duplicates are silently ignored. */
function saveStringListItem(key, value) {
    try {
        const current = loadStringList(key);
        if (!current.includes(value)) {
            current.push(value);
            localStorage.setItem(key, JSON.stringify(current));
        }
    }
    catch (error) {
        console.error(`Failed to save to "${key}" in storage:`, error);
    }
}
/** Remove a value from a persisted string list. */
function removeStringListItem(key, value) {
    try {
        const current = loadStringList(key).filter((s) => s !== value);
        localStorage.setItem(key, JSON.stringify(current));
    }
    catch (error) {
        console.error(`Failed to remove from "${key}" in storage:`, error);
    }
}
/** Load saved custom Kalshi series tickers from localStorage. */
function loadCustomSeries() {
    return loadStringList(CUSTOM_SERIES_STORAGE_KEY);
}
/** Save a custom Kalshi series ticker. Duplicates are silently ignored. */
function saveCustomSeries(ticker) {
    saveStringListItem(CUSTOM_SERIES_STORAGE_KEY, ticker);
}
/** Remove a custom Kalshi series ticker. */
function removeCustomSeries(ticker) {
    removeStringListItem(CUSTOM_SERIES_STORAGE_KEY, ticker);
}
/** Load saved custom events-page league prefixes (e.g. `KXNCAAMBB`). */
function loadCustomPrefixes() {
    return loadStringList(CUSTOM_PREFIXES_STORAGE_KEY);
}
/** Save a custom league prefix. Duplicates are silently ignored. */
function saveCustomPrefix(prefix) {
    saveStringListItem(CUSTOM_PREFIXES_STORAGE_KEY, prefix);
}
/** Remove a custom league prefix. */
function removeCustomPrefix(prefix) {
    removeStringListItem(CUSTOM_PREFIXES_STORAGE_KEY, prefix);
}
/** Load saved custom events-page market-type suffixes (e.g. `2HWINNER`). */
function loadCustomSuffixes() {
    return loadStringList(CUSTOM_SUFFIXES_STORAGE_KEY);
}
/** Save a custom market-type suffix. Duplicates are silently ignored. */
function saveCustomSuffix(suffix) {
    saveStringListItem(CUSTOM_SUFFIXES_STORAGE_KEY, suffix);
}
/** Remove a custom market-type suffix. */
function removeCustomSuffix(suffix) {
    removeStringListItem(CUSTOM_SUFFIXES_STORAGE_KEY, suffix);
}
// ============================================================================
// Kalshi Events-Page Prefix/Suffix Favourite (localStorage)
// ============================================================================
/** localStorage key for the events-page favourite prefix+suffix pair. */
const KALSHI_SERIES_FAVORITE_KEY = "kalshi_series_favorite";
/** Load the events-page favourite prefix/suffix pair (or `undefined`). */
function getKalshiSeriesFavorite() {
    try {
        const stored = localStorage.getItem(KALSHI_SERIES_FAVORITE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed &&
                typeof parsed.prefix === "string" &&
                parsed.prefix.length > 0 &&
                typeof parsed.suffix === "string" &&
                parsed.suffix.length > 0) {
                return { prefix: parsed.prefix, suffix: parsed.suffix };
            }
        }
    }
    catch (error) {
        console.error("Failed to load Kalshi series favorite from storage:", error);
    }
    return undefined;
}
/** Save the events-page favourite prefix/suffix pair. */
function saveKalshiSeriesFavorite(prefix, suffix) {
    try {
        localStorage.setItem(KALSHI_SERIES_FAVORITE_KEY, JSON.stringify({ prefix, suffix }));
    }
    catch (error) {
        console.error("Failed to save Kalshi series favorite to storage:", error);
    }
}
/** Clear the events-page favourite prefix/suffix pair. */
function clearKalshiSeriesFavorite() {
    try {
        localStorage.removeItem(KALSHI_SERIES_FAVORITE_KEY);
    }
    catch (error) {
        console.error("Failed to clear Kalshi series favorite from storage:", error);
    }
}
